import { isAxiosError } from 'axios';
import { t } from 'i18next';

import {
  ApErrorParams,
  ErrorCode,
  FEEDBACK_RATE_LIMIT,
  isNil,
  isObject,
  QuotaExceededParams,
} from '@activepieces/shared';

type ErrorMessage = {
  title: string;
  description: string;
};

const getQuotaExceededMessage = ({
  metric,
}: QuotaExceededParams['params']): ErrorMessage => {
  switch (metric) {
    case 'active-flows':
      return {
        title: t('Flow activation blocked'),
        description: t(
          'Your Smart plan active flows limit has been reached. Disable another active flow or upgrade to the Business plan.',
        ),
      };
    case 'projects':
      return {
        title: t('Project limit reached'),
        description: t(
          'You have reached the maximum number of projects allowed for your organization.',
        ),
      };
    case 'feedback':
      return {
        title: t('Feedback limit reached'),
        description: t(
          'You have reached the limit of {count} feedbacks per hour for this section. Please try again later.',
          { count: FEEDBACK_RATE_LIMIT },
        ),
      };
    default:
      return {
        title: t('Limit reached'),
        description: t('You have reached a limit of your plan.'),
      };
  }
};

const ERROR_CODES = new Set<unknown>(Object.values(ErrorCode));

const isApError = (value: unknown): value is ApErrorParams =>
  isObject(value) && ERROR_CODES.has(value.code);

// REST errors carry `{ code, params }` in the response body; websocket errors are that object itself.
// Undefined when the request got no response or the body is not an Activepieces error.
export const getApError = (error: unknown): ApErrorParams | undefined => {
  const body: unknown = isAxiosError(error) ? error.response?.data : error;
  return isApError(body) ? body : undefined;
};

// Maps an API error to a message the user can act on: retry, fix something, or contact support.
export const getErrorMessage = (error: unknown): ErrorMessage => {
  if (isAxiosError(error) && isNil(error.response)) {
    return {
      title: t('Server unreachable'),
      description: t('Check your internet connection and try again.'),
    };
  }
  const apError = getApError(error);
  switch (apError?.code) {
    case ErrorCode.QUOTA_EXCEEDED:
      return getQuotaExceededMessage(apError.params);
    case ErrorCode.FLOW_IN_USE:
      return {
        title: t('Flow being edited'),
        description: t(
          'Another user edited this flow less than a minute ago. Refresh the page to see their changes, then try again.',
        ),
      };
    case ErrorCode.TENANT_INACTIVE:
      return {
        title: t('Account inactive'),
        description: t(
          'Your account is inactive, so flows cannot be activated. Contact Kizeo support to reactivate it.',
        ),
      };
    case ErrorCode.TENANT_MIGRATING:
      return {
        title: t('Maintenance in progress'),
        description: t(
          'Your account is being updated. Try again in a few minutes.',
        ),
      };
    // Over HTTP an expired token is refreshed before onError runs; only the websocket,
    // which keeps the token it connected with, gets this far.
    case ErrorCode.INVALID_BEARER_TOKEN:
      return {
        title: t('Session expired'),
        description: t('Refresh the page to continue.'),
      };
    case ErrorCode.ENTITY_NOT_FOUND:
    case ErrorCode.FLOW_RUN_NOT_FOUND:
    case ErrorCode.STEP_NOT_FOUND:
      return {
        title: t('No longer available'),
        description: t(
          'This item may have been deleted. Refresh the page to see the latest data.',
        ),
      };
    case ErrorCode.AUTHORIZATION:
      return {
        title: t('Access denied'),
        description: t(
          "You don't have access to this resource. Refresh the page and try again.",
        ),
      };
    default:
      return {
        title: t('Something went wrong'),
        description: t(
          'Please try again later. If the problem persists, contact support.',
        ),
      };
  }
};
